import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './staffRateForm.module.scss'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'
import UserInput from '@/components/_custom/UserInput/userInput'
import { Daycare } from '@/types/Daycare'
import { CREATE_SECTION_GROUP, UPDATE_SECTION_GROUP } from '@/modules/API/staff/staffRate'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionsDeprecated } from '@/types/Select'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { SectionGroup } from '@/types/Section'

interface staffRateFormProps {
    handleClose: () => void
    daycare: Daycare
    create: boolean
    // EDIT
    staffRateId?: number
    staffRateName?: string
    setStaffRateName?: (value: string) => void
    staffRate?: string
    setStaffRate?: (value: string) => void
    staffRateSections?: Array<string>
    setStaffRateSections?: (value: Array<string>) => void
    refetchStaffRates: () => void
    hideSections?: boolean
    addNewSectionGroupOption?: (value: SectionGroup) => void
}

const StaffRateForm: FunctionComponent<staffRateFormProps> = ({
    handleClose,
    daycare,
    create,
    staffRateId,
    staffRateName,
    setStaffRateName,
    staffRate,
    setStaffRate,
    staffRateSections,
    setStaffRateSections,
    refetchStaffRates,
    hideSections,
    addNewSectionGroupOption
}: staffRateFormProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newStaffRateName, setNewStaffRateName] = useState<string>(staffRateName || '')
    const [newStaffRate, setNewStaffRate] = useState<string>(staffRate || '3')
    const [verifyStaffRate, setVerifyStaffRate] = useState<boolean>(true)

    const [newSectionsId, setNewSectionsId] = useState<Array<string>>(staffRateSections || [])

    const [createStaffRate] = useMutation(CREATE_SECTION_GROUP, {
        variables: {
            daycareId: daycare.id,
            sectionGroup: {
                name: newStaffRateName,
                rate: parseInt(newStaffRate),
                sections: newSectionsId.map((section) => parseInt(section))
            }
        },
        onCompleted: ({ createSectionGroup }) => {
            if (addNewSectionGroupOption)
                addNewSectionGroupOption({
                    id: createSectionGroup.id,
                    name: newStaffRateName,
                    rate: parseInt(newStaffRate),
                    sections: newSectionsId.map((section) => parseInt(section))
                })

            setNewStaffRateName('')
            setNewStaffRate('3')
            setNewSectionsId([])

            handleClose()

            showNotificationSuccess(t('staffLevelAdded'))
        },
        onError: () => {
            showNotificationError(t('errorAddedStaffRate'))
        }
    })

    const [updateStaffRate] = useMutation(UPDATE_SECTION_GROUP, {
        variables: {
            sectionGroupId: staffRateId,
            daycareId: Number(daycare.id) || 0,
            sectionGroup: {
                name: newStaffRateName,
                rate: parseInt(newStaffRate),
                sections: newSectionsId.map((section) => parseInt(section))
            }
        },
        onCompleted: () => {
            refetchStaffRates()

            setNewStaffRateName('')

            if (setStaffRateName) setStaffRateName(newStaffRateName)
            if (setStaffRate) setStaffRate(newStaffRate)
            if (setStaffRateSections) setStaffRateSections(newSectionsId)

            handleClose()

            showNotificationSuccess(t('staffLevelUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffRate'))
        }
    })

    const disabled: boolean =
        !verifyStaffRate ||
        (create
            ? newStaffRateName === ''
            : staffRateName === newStaffRateName &&
              staffRateSections === newSectionsId &&
              staffRate === newStaffRate &&
              JSON.stringify(staffRateSections) === JSON.stringify(newSectionsId))

    const sectionsOptions: SelectOptionsDeprecated = [
        {
            options:
                daycare.sections?.map((section) => ({
                    value: section.id.toString(),
                    name: section.name.valueOf()
                })) || []
        }
    ]

    return (
        <div>
            <div className={styles.InputContainer}>
                <div className={styles.Input}>
                    <UserInput
                        label={t('name')}
                        value={newStaffRateName}
                        type='text'
                        required
                        fullWidth
                        format={'pascal'}
                        className={styles.UserInput}
                        unallowedCharacters={'special'}
                        setValue={setNewStaffRateName}
                    />
                    <UserInput
                        label={t('childPerStaff')}
                        value={newStaffRate}
                        type='number'
                        required
                        fullWidth
                        format={'num'}
                        className={styles.UserInput}
                        unallowedCharacters={'alpha'}
                        setValue={setNewStaffRate}
                        setValid={setVerifyStaffRate}
                        minNumber={3}
                        maxNumber={15}
                        verify={'number'}
                    />
                </div>
                {!hideSections && (
                    <div className={styles.Input}>
                        <SelectMultiple
                            selectOptions={sectionsOptions}
                            selectValues={newSectionsId}
                            label={t('mySections')}
                            setSelectValues={setNewSectionsId}
                            className={`${styles.UserInput}`}
                        />
                    </div>
                )}
            </div>
            <DialogActions>
                <ButtonTextIcon type={'close'} onClick={handleClose} />
                <ButtonTextIcon
                    type={create ? 'add' : 'update'}
                    onClick={create ? createStaffRate : updateStaffRate}
                    disabled={disabled}
                    nowrap
                />
            </DialogActions>
        </div>
    )
}

export default StaffRateForm
